export type InvoiceStatusFilter = "all" | "Draft" | "FiledPendingSend" | "Sent" | "Unpaid" | "Overdue" | "Paid" | "Superseded";

export type EffectiveInvoiceStatus = "Draft" | "Filed" | "Sent" | "Overdue" | "Paid" | "Superseded";
export type InvoicePaymentState = "Paid" | "Not paid";

export const DEFAULT_INVOICE_STATUS_FILTER: InvoiceStatusFilter = "Unpaid";

type InvoiceStatusLike = {
  Status?: unknown;
  PaymentStatus?: unknown;
  PaidYN?: unknown;
  PaidDate?: unknown;
  DueDate?: unknown;
};

function normalizeInvoiceStatusValue(value?: unknown) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return "";

  if (normalized === "cancelled" || normalized === "canceled") return "superseded";
  if (normalized === "filed (pending send)" || normalized === "filed pending send") return "filed";

  return normalized;
}

function parseInvoiceDate(value?: unknown) {
  const text = String(value ?? "").trim();
  if (!text) return null;

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isTruthyPaidFlag(value?: unknown) {
  const normalized = normalizeInvoiceStatusValue(value);
  return normalized === "paid" || normalized === "yes" || normalized === "y" || normalized === "true" || normalized === "1";
}

function isPastDueInvoice(invoice: InvoiceStatusLike) {
  const dueDate = parseInvoiceDate(invoice?.DueDate);
  if (!dueDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  return dueDate.getTime() < today.getTime();
}

export function isPaidInvoice(invoice: InvoiceStatusLike) {
  return (
    isTruthyPaidFlag(invoice?.Status) ||
    isTruthyPaidFlag(invoice?.PaymentStatus) ||
    isTruthyPaidFlag(invoice?.PaidYN) ||
    parseInvoiceDate(invoice?.PaidDate) !== null
  );
}

export function getInvoicePaymentState(invoice: InvoiceStatusLike): InvoicePaymentState {
  return isPaidInvoice(invoice) ? "Paid" : "Not paid";
}

export function getEffectiveInvoiceStatus(invoice: InvoiceStatusLike): EffectiveInvoiceStatus {
  const normalizedStatus = normalizeInvoiceStatusValue(invoice?.Status);

  if (normalizedStatus === "superseded") return "Superseded";
  if (isPaidInvoice(invoice)) return "Paid";
  if (normalizedStatus === "filed") return "Filed";
  if (normalizedStatus === "overdue") return "Overdue";
  if (normalizedStatus === "sent") {
    return isPastDueInvoice(invoice) ? "Overdue" : "Sent";
  }

  return "Draft";
}

export function matchesStatusFilter(invoice: InvoiceStatusLike, statusFilter: InvoiceStatusFilter) {
  if (statusFilter === "all") return true;

  const effectiveStatus = getEffectiveInvoiceStatus(invoice);
  if (statusFilter === "Unpaid") {
    return effectiveStatus !== "Paid" && effectiveStatus !== "Superseded";
  }

  if (statusFilter === "FiledPendingSend") {
    return effectiveStatus === "Filed";
  }

  return effectiveStatus === statusFilter;
}
